import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { emailInbound } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DIRECTION_COLORS = {
  inbound:  'bg-blue-100 text-blue-800',
  outbound: 'bg-gray-100 text-gray-700',
};

const STATUS_COLORS = {
  sent:          'bg-green-100 text-green-800',
  failed:        'bg-red-100 text-red-800',
  received:      'bg-yellow-100 text-yellow-800',
  ai_processed:  'bg-purple-100 text-purple-800',
};

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-CA', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="btn text-sm px-3 py-1.5 border border-gray-300 hover:bg-gray-50 rounded-lg flex items-center gap-1.5"
    >
      {copied
        ? <><CheckIcon className="w-4 h-4 text-green-600" /> Copied!</>
        : <><ClipboardIcon className="w-4 h-4" /> Copy</>}
    </button>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
function ClipboardIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
    </svg>
  );
}

// ─── Setup instructions panel ────────────────────────────────────────────────

function SetupPanel({ emailAddr, webhookUrl, steps, expanded, toggle }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">SendGrid Setup (Production)</h3>
            <p className="text-xs text-gray-500">Configure inbound email for your live domain</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="mt-4 space-y-3">
            {(steps || []).map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center shrink-0 font-bold">
                  {i + 1}
                </div>
                <p className="text-sm text-gray-700 pt-0.5">{step}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Your inbound email address</p>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-sm text-blue-700 font-mono flex-1 break-all">
                  {emailAddr}
                </code>
                <CopyButton text={emailAddr} />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">SendGrid webhook URL (set this in the Inbound Parse settings)</p>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-sm font-mono flex-1 break-all text-gray-700">
                  https://your-server.com{webhookUrl}
                </code>
                <CopyButton text={`https://your-server.com${webhookUrl}`} />
              </div>
            </div>
          </div>

          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Local testing?</strong> Use the Simulate tool below — no SendGrid or domain setup needed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Simulate panel ───────────────────────────────────────────────────────────

const SAMPLE_EMAILS = [
  {
    label: 'Leaking pipe',
    from_email: 'unit301@example.com',
    subject: 'URGENT - Water leaking from bathroom pipe',
    body: 'Hi, my name is Sarah from unit 301. There is water leaking under the bathroom sink. It started this morning and is getting worse. The cabinet below is soaked. Can someone come urgently? My phone is 604-555-0123.',
  },
  {
    label: 'Broken elevator',
    from_email: 'resident@example.com',
    subject: 'Elevator not working',
    body: 'The elevator in Tower A has been out of service since yesterday. I am on the 8th floor and this is very inconvenient especially for residents with mobility issues. Please fix ASAP.',
  },
  {
    label: 'Parking issue',
    from_email: 'owner204@example.com',
    subject: 'Someone parked in my stall again - Unit 204',
    body: 'This is the third time this week someone has parked in stall P-12 which is assigned to unit 204. The car is a silver Toyota Corolla with no visible permit. Please address this.',
  },
  {
    label: 'Pest sighting',
    from_email: 'tenant@example.com',
    subject: 'Cockroach sighting in unit 108',
    body: 'I found cockroaches in my kitchen this morning. I am very concerned. Please send pest control as soon as possible. I live in unit 108.',
  },
];

function SimulatePanel({ onResult }) {
  const [form, setForm]     = useState({ from_email: '', subject: '', body: '' });
  const [loading, setLoading] = useState(false);

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const fillSample = (sample) => {
    setForm({ from_email: sample.from_email, subject: sample.subject, body: sample.body });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() && !form.body.trim()) {
      toast.error('Enter at least a subject or body');
      return;
    }
    setLoading(true);
    try {
      const res = await emailInbound.simulate(form);
      toast.success(
        res.data.work_order
          ? `Work order WO-${String(res.data.work_order.id).padStart(4, '0')} created!`
          : 'Email processed — no building found to create WO'
      );
      onResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Simulate Inbound Email</h3>
            <p className="text-xs text-gray-500">Test AI parsing without real email setup</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Sample emails */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Load a sample:</p>
          <div className="flex flex-wrap gap-2">
            {SAMPLE_EMAILS.map((s) => (
              <button
                key={s.label}
                onClick={() => fillSample(s)}
                className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-blue-50 hover:text-blue-700 rounded-lg border border-gray-200 transition-colors"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="label">From Email</label>
            <input
              type="email"
              className="input"
              value={form.from_email}
              onChange={set('from_email')}
              placeholder="resident@example.com"
            />
          </div>
          <div>
            <label className="label">Subject</label>
            <input
              className="input"
              value={form.subject}
              onChange={set('subject')}
              placeholder="e.g. Leaking pipe in unit 302"
            />
          </div>
          <div>
            <label className="label">Body</label>
            <textarea
              className="input"
              rows={5}
              value={form.body}
              onChange={set('body')}
              placeholder="Describe the issue..."
              style={{ height: 'auto' }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading
              ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg> Processing…</>
              : '🤖 Process with AI'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── AI Result panel ─────────────────────────────────────────────────────────

function AIResultPanel({ result }) {
  if (!result) return null;
  const { work_order, ai_result } = result;

  return (
    <div className="bg-white rounded-xl border-2 border-green-200 overflow-hidden">
      <div className="px-5 py-4 bg-green-50 border-b border-green-200">
        <h3 className="font-semibold text-green-900 text-sm flex items-center gap-2">
          <span>✅</span> AI Parse Result
        </h3>
      </div>
      <div className="p-5 space-y-4">
        {work_order && (
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-2">
              Work Order Created
            </p>
            <p className="text-sm font-bold text-gray-900">
              WO-{String(work_order.id).padStart(4, '0')}: {work_order.title}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {work_order.building_name} · Priority: {work_order.priority}
              {work_order.unit_number ? ` · Unit ${work_order.unit_number}` : ''}
            </p>
            <Link
              to="/workorders"
              className="inline-block mt-2 text-xs text-blue-600 hover:underline font-medium"
            >
              View in Work Orders →
            </Link>
          </div>
        )}

        {ai_result && (
          <div>
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">
              AI Extracted Fields
            </p>
            <div className="space-y-1">
              {Object.entries(ai_result).map(([k, v]) => {
                if (k === 'building_id' || v === null || v === undefined) return null;
                return (
                  <div key={k} className="flex gap-2 text-sm">
                    <span className="text-gray-500 w-40 shrink-0">
                      {k.replace(/_/g, ' ')}
                    </span>
                    <span className="text-gray-900 font-medium break-all">
                      {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Email log list ───────────────────────────────────────────────────────────

function LogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmt(log.created_at)}</td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIRECTION_COLORS[log.direction]}`}>
            {log.direction === 'inbound' ? '📥 In' : '📤 Out'}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{log.subject || '—'}</td>
        <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell truncate max-w-[140px]">
          {log.direction === 'inbound' ? log.from_email : log.to_email}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-600'}`}>
            {log.status}
          </span>
        </td>
        <td className="px-4 py-3">
          {log.work_order_id && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">
              WO-{String(log.work_order_id).padStart(4, '0')}
            </span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-blue-50">
          <td colSpan={6} className="px-5 py-3 text-sm text-gray-700">
            {log.body_preview
              ? <p className="whitespace-pre-wrap text-xs">{log.body_preview}…</p>
              : <p className="text-xs text-gray-400 italic">No body preview available</p>}
            {log.ai_result && (
              <details className="mt-2">
                <summary className="text-xs text-blue-600 cursor-pointer font-medium">
                  AI parsed fields
                </summary>
                <pre className="text-xs mt-1 bg-white rounded p-2 overflow-auto">
                  {JSON.stringify(log.ai_result, null, 2)}
                </pre>
              </details>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function LogsTable({ logs, loading }) {
  if (loading) {
    return (
      <div className="text-center py-8 text-gray-400">
        <svg className="animate-spin h-6 w-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading logs…
      </div>
    );
  }
  if (!logs.length) {
    return (
      <div className="text-center py-10 text-gray-400 text-sm">
        No email logs yet. Use the Simulate tool to test inbound email parsing.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-xs font-medium text-gray-500">Time</th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500">Dir</th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500">Subject</th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500 hidden sm:table-cell">From / To</th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500">Status</th>
            <th className="px-4 py-3 text-xs font-medium text-gray-500">WO</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {logs.map((log) => <LogRow key={log.id} log={log} />)}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmailHub() {
  const [addressData, setAddressData]   = useState(null);
  const [logs, setLogs]                 = useState([]);
  const [logsLoading, setLogsLoading]   = useState(true);
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [aiResult, setAiResult]         = useState(null);
  const [filterDir, setFilterDir]       = useState('');  // '' | 'inbound' | 'outbound'

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await emailInbound.logs({ direction: filterDir || undefined, limit: 100 });
      setLogs(res.data);
    } catch {
      /* silent */
    } finally {
      setLogsLoading(false);
    }
  }, [filterDir]);

  useEffect(() => {
    emailInbound.address()
      .then((r) => setAddressData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSimulateResult = (result) => {
    setAiResult(result);
    loadLogs();
  };

  const inboundCount  = logs.filter((l) => l.direction === 'inbound').length;
  const outboundCount = logs.filter((l) => l.direction === 'outbound').length;
  const woCreated     = logs.filter((l) => l.ai_parsed).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Hub</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Inbound email parsing · Status notifications · Reply tracking
          </p>
        </div>
        <button
          onClick={loadLogs}
          className="btn border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm flex items-center gap-2 self-start sm:self-auto"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Your unique email address */}
      {addressData && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 mb-6 text-white">
          <p className="text-blue-200 text-xs uppercase tracking-wide mb-1 font-semibold">
            Your Unique Inbound Email Address
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <code className="text-lg sm:text-xl font-mono font-bold break-all flex-1">
              {addressData.email}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(addressData.email);
                toast.success('Email address copied!');
              }}
              className="bg-white/20 hover:bg-white/30 text-white text-sm px-4 py-2 rounded-lg transition-colors shrink-0 font-medium"
            >
              Copy
            </button>
          </div>
          <p className="text-blue-200 text-xs mt-3">
            Share this address with residents. Every email sent here is automatically
            parsed by AI and converted into a work order.
          </p>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Emails Received',   value: inboundCount,  color: 'border-blue-200 bg-blue-50',   text: 'text-blue-700' },
          { label: 'Emails Sent',       value: outboundCount, color: 'border-gray-200 bg-white',     text: 'text-gray-700' },
          { label: 'WOs Auto-Created',  value: woCreated,     color: 'border-green-200 bg-green-50', text: 'text-green-700' },
        ].map(({ label, value, color, text }) => (
          <div key={label} className={`rounded-xl border p-4 ${color}`}>
            <p className="text-xs text-gray-500">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${text}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Simulate tool */}
        <SimulatePanel onResult={handleSimulateResult} />

        {/* Setup instructions */}
        {addressData && (
          <SetupPanel
            emailAddr={addressData.email}
            webhookUrl={addressData.webhook_url}
            steps={addressData.setup_steps}
            expanded={setupExpanded}
            toggle={() => setSetupExpanded(!setupExpanded)}
          />
        )}
      </div>

      {/* AI result from last simulation */}
      {aiResult && (
        <div className="mb-6">
          <AIResultPanel result={aiResult} />
        </div>
      )}

      {/* Email log */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-5 py-4 border-b border-gray-100 gap-3">
          <h2 className="font-semibold text-gray-900">Email Log</h2>
          <div className="flex gap-2">
            {[
              { label: 'All',      value: '' },
              { label: 'Inbound',  value: 'inbound' },
              { label: 'Outbound', value: 'outbound' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setFilterDir(value)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  filterDir === value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <LogsTable logs={logs} loading={logsLoading} />
      </div>

      {/* How it works */}
      <div className="mt-6 bg-gray-50 rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm">How Email Integration Works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { step: '1', icon: '📧', title: 'Resident Emails', desc: 'Resident sends an email to your unique @stratahub.ca address' },
            { step: '2', icon: '🤖', title: 'AI Parses It',    desc: 'Llama AI extracts title, priority, unit number, category and building' },
            { step: '3', icon: '📋', title: 'WO Created',      desc: 'A work order is automatically created in your StrataPulse account' },
            { step: '4', icon: '🔔', title: 'You\'re Notified', desc: 'You get an email notification with the work order details' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="text-center">
              <div className="text-2xl mb-2">{icon}</div>
              <p className="text-sm font-semibold text-gray-900 mb-1">{title}</p>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
